import React from 'react';
import { JcLoadingPlaceholder } from '../JcLoadingPlaceholder';
import { JcDataErrorNotice } from './JcDataErrorNotice';
import { JcAsyncResult } from '../../data/JcAsyncResult';
import { isLoading } from '../../utils/JcLoading';
import { JcCommonProps } from '../../utils/JcUtils';

export interface JcAsyncBoundaryProps<T> extends JcCommonProps {
    readonly result: JcAsyncResult<T>;
    readonly loadingId: string;
    readonly children: (value: T) => React.ReactElement;
}

/**
 * Resolves a {@link JcAsyncResult}: an inline {@link JcDataErrorNotice} on error,
 * a {@link JcLoadingPlaceholder} while loading, else the rendered value. Common
 * props (e.g. `className`) flow to the error/loading states so they sit in place.
 * Place it inside page chrome to keep that chrome mounted across every state.
 */
export function JcAsyncBoundary<T>(
    { result, loadingId, children, ...commonProps }: JcAsyncBoundaryProps<T>,
): React.ReactElement {
    const { value, error, retry } = result;
    if (error) return <JcDataErrorNotice {...commonProps} error={error} onRetry={retry} />;
    if (isLoading(value)) return <JcLoadingPlaceholder {...commonProps} id={loadingId} />;
    return children(value);
}
